/**
 * The locked echelon spacing figures. Ruling 37, 19 September 2026.
 *
 * Doc 03 §7.11. Each figure carries its citation, and **none is to be applied
 * outside its scope.** That is the whole point of this file: before Ruling 37
 * there was no figure at all and `checkPlausibility` could flag nothing;
 * the danger now is the opposite one, of reaching for whichever figure is
 * nearest to hand and calling a distance implausible against a rule that was
 * never about it.
 *
 * So a figure is never selected by guesswork. The caller states which scope
 * it is measuring, and if it does not state one, nothing is flagged. A
 * distance between two control measures on a trace is not a vehicle interval,
 * and no figure here is about the spacing of control measures.
 */

/**
 * What each figure governs. A scope is a claim about what is being measured,
 * and it comes from the caller — never from the shape of the number.
 */
export type SpacingScope =
  | "afv-dispersion-defensive"
  | "interval-between-squadrons"
  | "line-formation-vehicle-spacing"
  | "minimum-dispersion-passive-air-defence"
  | "mutual-support-between-vehicles"
  | "static-direct-fire-frontage-per-heavy-afv"
  | "vehicle-interval-in-troop"
  | "zone-recce-frontage-per-troop";

export interface SpacingFigure {
  /** Where the figure comes from. Every figure has one. */
  readonly citation: string;
  /** Upper bound in metres, or null where the source states none. */
  readonly maximumM: number | null;
  /** Lower bound in metres, or null where the source states none. */
  readonly minimumM: number | null;
  /**
   * Present where the source does not reduce to a pair of metres. Such a
   * figure is reported and **never used to flag**, because applying it would
   * mean supplying the part the source left out.
   */
  readonly qualifier?: string;
  readonly scope: SpacingScope;
  /** What the figure is about, in the source's own terms. */
  readonly what: string;
}

export const SPACING_FIGURES: readonly SpacingFigure[] = Object.freeze([
  {
    scope: "vehicle-interval-in-troop",
    what: "Vehicle interval within a troop",
    minimumM: 100,
    maximumM: 100,
    citation: "ACRIB para 0309",
  },
  {
    scope: "interval-between-squadrons",
    what: "Interval between squadrons",
    minimumM: 200,
    maximumM: 400,
    citation: "ACRIB para 0309",
  },
  {
    scope: "line-formation-vehicle-spacing",
    what: "Line formation, vehicle spacing",
    minimumM: 50,
    maximumM: 100,
    citation: "B-GL-305-008 para 0310",
  },
  {
    scope: "afv-dispersion-defensive",
    what: "AFV dispersion, defensive position",
    minimumM: null,
    maximumM: 400,
    citation: "B-GL-305-008 para 0333-0336",
  },
  {
    scope: "minimum-dispersion-passive-air-defence",
    what: "Minimum dispersion, passive air defence",
    minimumM: 50,
    maximumM: null,
    citation: "B-GL-305-008 para 0672-0674",
  },
  {
    scope: "zone-recce-frontage-per-troop",
    what: "Zone recce, frontage per troop",
    minimumM: 2500,
    maximumM: 4000,
    citation: "B-GL-305-008 para 0518-0527",
  },
  {
    scope: "mutual-support-between-vehicles",
    what: "Mutual support between vehicles",
    minimumM: null,
    maximumM: 2000,
    qualifier:
      "Half visual range or half effective weapons range, whichever is less; up to 2 km for heavy AFVs. The governing value is derived from ranges this repository does not hold, so the figure is reported and never used to flag.",
    citation: "ACRIB para 0379",
  },
  {
    scope: "static-direct-fire-frontage-per-heavy-afv",
    what: "Static direct fire, frontage per heavy AFV",
    minimumM: 1000,
    maximumM: null,
    qualifier:
      "More than 1 km, to select primary, secondary and alternate positions. The figure is a requirement on the ground a vehicle needs, not a bound on a distance between two points, so it is reported and never used to flag.",
    citation: "ACRIB para 0379",
  },
]);

export function spacingFigureFor(scope: SpacingScope): SpacingFigure {
  /* v8 ignore next -- the scope union has an entry for every figure */
  return SPACING_FIGURES.find((figure) => figure.scope === scope) ?? never();
}

/* v8 ignore next 3 -- unreachable while the union and the table agree */
function never(): never {
  throw new Error("no figure for that scope");
}

/**
 * Measurements Ruling 37 records as **not locked**. No figure for any of them
 * exists in the sources deployed, and none is to be derived from the figures
 * that are locked.
 *
 * They are named rather than left implicit because they are the ones somebody
 * will reach for: they are the obvious questions to ask of a trace, and the
 * answer is that doctrine as deployed does not answer them.
 */
export const NOT_LOCKED: readonly string[] = Object.freeze([
  "Squadron frontage in the advance",
  "Troop frontage in a guard",
  "Spacing between areas of interest",
  "LD-to-LOE depth",
]);

export type SpacingVerdict =
  | "above"
  | "below"
  | "no-locked-figure"
  | "not-applicable"
  | "within";

export interface SpacingJudgement {
  /** Why, in one line, for a report that has to stand on its own. */
  readonly because: string;
  /** Null where no figure was applied. */
  readonly citation: string | null;
  readonly distanceM: number;
  /** Null where the pair is not the thing any source describes. */
  readonly scope: SpacingScope | null;
  readonly verdict: SpacingVerdict;
}

export const NO_LOCKED_FIGURE_BECAUSE =
  "No locked figure covers this pair. Two markers on a trace are not two vehicles, and Ruling 37 forbids applying a figure outside its scope. The distance is reported and nothing is flagged.";

/**
 * The verdict for a pair no figure covers. Ruling 37: report the distance and
 * `NO_LOCKED_FIGURE`, rather than reaching for the nearest figure to hand.
 */
export function noLockedFigure(distanceM: number): SpacingJudgement {
  return {
    because: NO_LOCKED_FIGURE_BECAUSE,
    citation: null,
    distanceM,
    scope: null,
    verdict: "no-locked-figure",
  };
}

/**
 * Judges one distance against one figure, **in the scope the caller states**.
 *
 * A figure carrying a qualifier judges nothing: the source did not reduce it
 * to metres, and completing it here would be inventing the part it left out.
 * Hard rule 4.
 */
export function judgeSpacing(
  distanceM: number,
  scope: SpacingScope | null,
): SpacingJudgement {
  if (scope === null) return noLockedFigure(distanceM);
  const figure = spacingFigureFor(scope);
  if (figure.qualifier !== undefined) {
    return {
      because: figure.qualifier,
      citation: figure.citation,
      distanceM,
      scope,
      verdict: "not-applicable",
    };
  }
  const below = figure.minimumM !== null && distanceM < figure.minimumM;
  const above = figure.maximumM !== null && distanceM > figure.maximumM;
  const bound = (value: number | null): string =>
    value === null ? "none stated" : `${String(value)} m`;
  return {
    because: `${figure.what}: ${bound(figure.minimumM)} to ${bound(figure.maximumM)}, ${figure.citation}.`,
    citation: figure.citation,
    distanceM,
    scope,
    verdict: below ? "below" : above ? "above" : "within",
  };
}
